/**
 * Number Formatting Utilities
 *
 * Centralized number formatting functions to ensure consistency across
 * the application in trial summaries, reports, and data displays.
 */

/**
 * Format configuration for 2 decimal places
 */
export const TWO_DECIMAL_FORMAT = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} as const;

/**
 * Format configuration for no decimal places
 */
export const NO_DECIMAL_FORMAT = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
} as const;

/**
 * Determines the appropriate number of decimal places based on the unit type
 *
 * @param unit - The measurement unit (e.g., "kg/ha", "l/ha", "seeds/ha")
 * @param valueType - The type of value being formatted ("rate", "total", or "percentage")
 * @returns Number of decimal places to use
 */
export const getDecimalPlaces = (
  unit: string,
  valueType: "rate" | "total" | "percentage" = "rate"
): number => {
  const lowerUnit = unit.toLowerCase();

  // Seed counts (not kg) should have no decimals
  if (
    lowerUnit.includes("seeds") &&
    !lowerUnit.includes("kg") &&
    valueType === "total"
  ) {
    return 0;
  }

  // Seeds per m² rates should have 2 decimals
  if (lowerUnit.includes("seeds/m")) {
    return 2;
  }

  // Kg-based units should always have 2 decimals
  if (lowerUnit.includes("kg")) {
    return 2;
  }

  // Liter-based units should always have 2 decimals
  if (lowerUnit.includes("l") || lowerUnit.includes("liter")) {
    return 2;
  }

  // Percentages should use 2 decimals
  if (valueType === "percentage") {
    return 2;
  }

  // Default to 2 decimals for consistency
  return 2;
};

/**
 * Format a number with appropriate decimal places based on unit and value type
 *
 * @param value - The number to format
 * @param unit - The measurement unit
 * @param valueType - The type of value being formatted
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number,
  unit: string,
  valueType: "rate" | "total" | "percentage" = "rate"
): string => {
  const decimals = getDecimalPlaces(unit, valueType);
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format a number with exactly 2 decimal places
 * Use for: kg/ha, l/ha, nutrient amounts, fertilizer amounts
 *
 * @param value - The number to format
 * @returns Formatted number string with 2 decimals
 */
export const formatWithTwoDecimals = (value: number): string => {
  return value.toLocaleString(undefined, TWO_DECIMAL_FORMAT);
};

/**
 * Round a number to exactly 2 decimal precision as a numeric value.
 * Use when storing values that should match API payload precision.
 *
 * @param value - The number to round
 * @returns Rounded number with 2 decimal precision
 */
export const roundToTwoDecimals = (value: number): number => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Format a number with no decimal places
 * Use for: seed counts, whole units
 *
 * @param value - The number to format
 * @returns Formatted number string with no decimals
 */
export const formatWithNoDecimals = (value: number): string => {
  return value.toLocaleString(undefined, NO_DECIMAL_FORMAT);
};

/**
 * Get formatting configuration object based on unit
 * For use with Intl.NumberFormat or similar APIs
 *
 * @param unit - The measurement unit
 * @param valueType - The type of value being formatted
 * @returns Format configuration object
 */
export const getFormatConfig = (
  unit: string,
  valueType: "rate" | "total" | "percentage" = "rate"
): { minimumFractionDigits: number; maximumFractionDigits: number } => {
  const decimals = getDecimalPlaces(unit, valueType);
  return {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };
};
